'use client';

import { useState } from 'react';
import {
  demoLocationIdForBusinessType,
  demoLocationForBusinessType,
} from '@/lib/demo-business-types';
import type { IDemoLocation } from '@/interfaces/demoBusinessTypes';
import { getThemeBackgroundClass } from '@/lib/theme';
import { useTheme } from '@/contexts/ThemeContext';
import TitleBarSub from '@/components/TitleBarSub';
import { environment } from '@/config/environment';
import DemoAppFrame from '@/components/DemoAppFrame';
import { trackInteraction } from '@/lib/analytics';

interface BusinessTypeOption {
  id: string;
  emoji: string;
  name: string;
}

interface InteractiveBookingDemoProps {
  language: 'en' | 'zh';
  businessTypes: BusinessTypeOption[];
  showTablet?: boolean;
  showTitleBar?: boolean;
}

const DEMO_BASE_URL = environment.api.demoAppUrl;

export default function InteractiveBookingDemo({
  language,
  businessTypes,
  showTablet = false,
  showTitleBar = false,
}: InteractiveBookingDemoProps) {
  const { resolvedTheme } = useTheme();
  const backgroundClasses = getThemeBackgroundClass(null, 'primary');
  const [activeKey, setActiveKey] = useState(
    businessTypes.some(type => type.id === 'hairdressers')
      ? 'hairdressers'
      : businessTypes[0]?.id || ''
  );
  const demoBaseUrl = DEMO_BASE_URL;
  const activeBusinessType =
    businessTypes.find(type => type.id === activeKey) ?? businessTypes[0];
  const configuredLocation = demoLocationForBusinessType(
    activeBusinessType?.id || ''
  );
  const fallbackLocation = demoLocationForBusinessType('hairdressers')!;
  const active: IDemoLocation = configuredLocation ?? {
    key: activeBusinessType?.id || fallbackLocation.key,
    businessTypeId: activeBusinessType?.id || fallbackLocation.businessTypeId,
    businessTypeName: {
      en: activeBusinessType?.name || fallbackLocation.businessTypeName.en,
      zh: activeBusinessType?.name || fallbackLocation.businessTypeName.zh,
    },
    emoji: activeBusinessType?.emoji || fallbackLocation.emoji,
    locationId: fallbackLocation.locationId,
    bookingPage: fallbackLocation.bookingPage,
    location: activeBusinessType?.name || fallbackLocation.location,
    headline: {
      en: 'See how the complete customer booking journey works.',
      zh: '查看完整的顧客預約流程如何運作。',
    },
    points: [
      {
        en: 'Customers can discover services and browse live availability.',
        zh: '顧客可探索服務並瀏覽即時空檔。',
      },
      {
        en: 'Bookings, customer details and payments stay connected.',
        zh: '預約、顧客資料與付款資訊保持連動。',
      },
      {
        en: 'The experience adapts to each location and business type.',
        zh: '體驗會依每個場館與產業類型調整。',
      },
    ],
  };
  const activeLocationId = demoLocationIdForBusinessType(
    activeBusinessType?.id || fallbackLocation.businessTypeId
  );
  const iframeLanguage = language === 'zh' ? 'cn' : 'en';
  const fullDemoUrl = `${demoBaseUrl}?locationId=${activeLocationId}&embed=1&full=true&overrideLanguage=${iframeLanguage}&overrideAppearance=${resolvedTheme}`;
  const demoLink = showTablet ? fullDemoUrl : `/${language}/demo/`;

  const t =
    language === 'en'
      ? {
          eyebrow: 'BUILT FOR THE WAY YOU WORK',
          title: showTablet
            ? 'Try the real booking experience.'
            : 'Pick an industry. Use the real booking system.',
          description: showTablet
            ? 'Choose an industry, then explore the same live customer booking system on a phone or tablet. Every location has its own services, staff, rooms and availability.'
            : 'Each tab opens a different location inside our live demo business, with its own services, staff, rooms and availability.',
          live: 'LIVE DEMOAPT',
          frameTitle: 'Interactive Omcean Booking customer demo',
          open: showTablet ? 'Open in a new tab' : 'Open full demo',
          note: showTablet
            ? 'This is the real customer booking app—not a screenshot. The phone and tablet below share the same customer, location and live booking data.'
            : 'This is the real customer booking app—not a screenshot. You can browse services, availability and the booking flow inside the phone.',
          context: 'CUSTOMER BOOKING · LIVE DATA',
          phone: 'Phone',
          tablet: 'Tablet',
          tabletPreview: 'Tablet booking experience',
        }
      : {
          eyebrow: '為您的營運方式而打造',
          title: showTablet
            ? '親自體驗真正的預約流程'
            : '選擇產業，直接操作真正的預約系統',
          description: showTablet
            ? '選擇產業，並使用手機或平板操作同一套即時顧客預約系統。每個場館都有自己的服務、人員、空間與即時空檔。'
            : '每個分頁都會開啟體驗商家中的不同場館，顯示各自的服務、人員、空間與即時空檔。',
          live: '即時 DEMOAPT',
          frameTitle: 'Omcean Booking 顧客端互動體驗',
          open: showTablet ? '在新分頁開啟' : '開啟完整體驗',
          note: showTablet
            ? '這是真正的顧客預約系統，不是畫面截圖。下方的手機和平板會共用同一位顧客、場館與即時預約資料。'
            : '這是真正的顧客預約系統，不是畫面截圖。您可以直接在手機框內瀏覽服務、空檔與預約流程。',
          context: '顧客預約 · 即時資料',
          phone: '手機',
          tablet: '平板',
          tabletPreview: '平板預約體驗',
        };
  const HeadingTag = showTitleBar ? 'h1' : 'h2';

  return (
    <section
      className={`${backgroundClasses} relative overflow-hidden pb-20 lg:pb-28 ${showTitleBar ? 'pt-[22px]' : 'pt-12 lg:pt-16'}`}
    >
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {showTitleBar && (
          <TitleBarSub language={language} className="mb-12 lg:mb-16" />
        )}
        <div className="mx-auto max-w-3xl text-center">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-[var(--color-muted-foreground)]">
            {t.eyebrow}
          </div>
          <HeadingTag className="mt-5 text-4xl font-black tracking-[-0.035em] text-[var(--color-foreground)] sm:text-5xl lg:text-[3.4rem] lg:leading-[1.04]">
            {t.title}
          </HeadingTag>
          <p className="escape mx-auto mt-5 max-w-2xl text-lg leading-8 text-[var(--color-foreground-secondary)]">
            {t.description}
          </p>
        </div>

        <div
          className="mt-9 flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center"
          role="group"
          aria-label={language === 'en' ? 'Demo industries' : '體驗產業'}
        >
          {businessTypes.map(businessType => {
            const selected = businessType.id === activeBusinessType?.id;
            return (
              <button
                key={businessType.id}
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  if (!selected) {
                    trackInteraction(
                      'booking_demo_business_switched',
                      businessType.id,
                      { from: activeBusinessType?.id ?? null, language }
                    );
                  }
                  setActiveKey(businessType.id);
                }}
                className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-semibold transition ${selected ? 'border-[#3880ff]/30 bg-white text-[#2563eb] shadow-md ring-2 ring-[#3880ff]/10 dark:bg-gray-800 dark:text-[#7fb0ff]' : 'border-[var(--color-border-secondary)] bg-white/70 text-[var(--color-foreground-secondary)] hover:-translate-y-0.5 hover:border-[#3880ff]/50 hover:bg-white hover:text-[#2563eb] dark:bg-gray-800/70'}`}
              >
                <span className="text-lg" aria-hidden="true">
                  {businessType.emoji || '✨'}
                </span>
                {businessType.name}
              </button>
            );
          })}
        </div>

        <div className="mt-8 overflow-hidden rounded-[2.2rem] border border-[var(--color-card-border)] bg-[var(--color-background-secondary)] shadow-[0_32px_90px_-55px_rgba(15,23,42,0.5)]">
          <div className="grid items-center gap-10 px-5 py-8 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:px-14 lg:py-14">
            <div className="mx-auto w-full max-w-[390px]">
              <div className="mb-4 flex items-center justify-between px-2">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  {t.live}
                </div>
                <a
                  href={demoLink}
                  target={showTablet ? '_blank' : undefined}
                  rel={showTablet ? 'noreferrer' : undefined}
                  onClick={() =>
                    trackInteraction(
                      'booking_demo_opened_full',
                      activeBusinessType?.id ?? null,
                      { language }
                    )
                  }
                  className="text-sm font-bold text-[var(--link-text)] hover:underline"
                >
                  {t.open} ↗
                </a>
              </div>
              <div className="relative mx-auto w-full max-w-[370px]">
                <DemoAppFrame
                  language={language}
                  locationId={activeLocationId}
                  title={`${t.frameTitle}: ${active.location}`}
                  trackingSurface="home_industry_demo"
                  className="block h-[720px] max-h-[76vh] min-h-[620px] w-full rounded-[38px] border-0 bg-[var(--color-background)]"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -inset-[10px] z-10 rounded-[48px] border-[10px] border-[#111214] shadow-[0_25px_60px_-20px_rgba(0,0,0,0.45)]"
                />
              </div>
            </div>

            <div className="lg:py-8">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
                  {t.context}
                </div>
                <div className="mt-3 text-sm font-semibold text-[var(--link-text)]">
                  {active.location}
                </div>
                <h3 className="mt-3 max-w-xl text-3xl font-black tracking-[-0.03em] text-[var(--color-foreground)] sm:text-4xl">
                  {active.headline[language]}
                </h3>
              </div>
              <div>
                <div className="mt-8 space-y-5">
                  {active.points.map(point => (
                    <div key={point.en} className="flex gap-3.5">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-xs font-black text-white">
                        ✓
                      </span>
                      <p className="escape text-base leading-7 text-[var(--color-foreground-secondary)]">
                        {point[language]}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="escape mt-8 max-w-xl border-t border-[var(--color-border)] pt-6 text-sm leading-6 text-[var(--color-muted-foreground)]">
                  {t.note}
                </p>
              </div>
            </div>
          </div>
        </div>

        {showTablet && (
          <div className="mt-10 overflow-hidden rounded-[2.2rem] border border-[var(--color-card-border)] bg-[var(--color-background-secondary)] px-4 py-8 shadow-[0_32px_90px_-55px_rgba(15,23,42,0.5)] sm:px-7 lg:px-12 lg:py-12">
            <div className="mb-6 flex flex-col gap-2 px-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  {t.live} · {t.tablet}
                </div>
                <h3 className="mt-2 text-2xl font-black tracking-[-0.025em] text-[var(--color-foreground)] sm:text-3xl">
                  {t.tabletPreview}
                </h3>
              </div>
              <div className="text-sm font-semibold text-[var(--link-text)]">
                {active.location}
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-[1096px]">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-[-8px] z-20 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-[#34363a]"
              />
              <DemoAppFrame
                language={language}
                locationId={activeLocationId}
                title={`${t.tabletPreview}: ${active.location}`}
                trackingSurface="home_industry_demo"
                className="block h-[720px] max-h-[78vh] min-h-[600px] w-full rounded-[24px] border-0 bg-[var(--color-background)]"
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -inset-[12px] z-10 rounded-[36px] border-[12px] border-[#111214] shadow-[0_25px_60px_-20px_rgba(0,0,0,0.45)]"
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
